import { useCallback, useEffect, useState } from 'react';
import { navigate, useRoute } from '@/ui/router';
import { Banner, EmptyState } from '@/ui/components/bits';
import { balance, verifyBalance, type BalanceResult } from '@/domain/balance';
import { balanceRedox, type Medium, type RedoxOutcome } from '@/domain/redox';
import {
  checkCoefficients,
  drawBalancingPractice,
  REACTION_TYPE_LABELS,
  type BalancingPractice,
  type ReactionType,
} from '@/domain/reactions';
import { newSeed } from '@/domain/random';

type Tab = 'balance' | 'practice' | 'redox';

export function BalancerScreen() {
  const route = useRoute();
  const tab = (route.params.get('tab') ?? 'balance') as Tab;

  return (
    <div className="stack">
      <h1>Equation balancer</h1>

      <div className="seg" role="tablist">
        {(
          [
            ['balance', 'Balance'],
            ['practice', 'Practice'],
            ['redox', 'Redox'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className="seg__btn"
            role="tab"
            aria-pressed={tab === key}
            onClick={() => navigate('/balance', key === 'balance' ? {} : { tab: key })}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'balance' ? <BalanceTab /> : null}
      {tab === 'practice' ? <PracticeTab /> : null}
      {tab === 'redox' ? <RedoxTab /> : null}
    </div>
  );
}

function BalanceTab() {
  const [input, setInput] = useState('C3H8 + O2 -> CO2 + H2O');
  const [result, setResult] = useState<BalanceResult | null>(null);

  const run = () => setResult(balance(input));

  return (
    <div className="stack">
      <label className="label" htmlFor="equation">
        An equation
      </label>
      <input
        id="equation"
        className="input mono"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') run();
        }}
        placeholder="Fe + O2 -> Fe2O3"
      />
      <span className="faint">
        Separate species with a space, a plus, and a space. Charges are fine: write Fe3+ or SO4 2-.
      </span>

      <button className="btn btn--primary" onClick={run}>
        Balance it
      </button>

      {result?.ok ? (
        <>
          <div className="card stack">
            <span className="flashcard__label">Balanced</span>
            <div className="mono" style={{ fontSize: '1.15rem', overflowWrap: 'anywhere' }}>
              {result.equation}
            </div>
            {result.hasCharges ? (
              <span className="faint">Charge was balanced too, as a row of its own.</span>
            ) : null}
          </div>
          <AtomTally result={result} />
        </>
      ) : null}

      {result && !result.ok ? (
        <Banner kind={result.reason === 'invalid' ? 'error' : 'warn'}>
          <strong>
            {result.reason === 'multiple-solutions'
              ? 'More than one answer'
              : result.reason === 'no-solution'
                ? 'No balance exists'
                : 'Could not read that'}
          </strong>
          <div>{result.message}</div>
        </Banner>
      ) : null}
    </div>
  );
}

function AtomTally({ result }: { result: Extract<BalanceResult, { ok: true }> }) {
  const check = verifyBalance(
    { reactants: result.reactants, products: result.products },
    result.coefficients,
  );
  return (
    <div className="card stack">
      <span className="flashcard__label">Atom count, both sides</span>
      {[...check.perElement.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([element, v]) => (
        <div key={element} className="spread">
          <span className="mono">{element}</span>
          <span className="mono" style={{ color: v.left === v.right ? 'var(--correct)' : 'var(--incorrect)' }}>
            {v.left} = {v.right}
          </span>
        </div>
      ))}
      {result.hasCharges ? (
        <div className="spread">
          <span className="mono">charge</span>
          <span
            className="mono"
            style={{ color: check.charge.left === check.charge.right ? 'var(--correct)' : 'var(--incorrect)' }}
          >
            {check.charge.left} = {check.charge.right}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function PracticeTab() {
  const [type, setType] = useState<ReactionType | 'all'>('all');
  const [practice, setPractice] = useState<BalancingPractice | null>(null);
  const [typed, setTyped] = useState<string[]>([]);
  const [verdict, setVerdict] = useState<{ correct: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const draw = useCallback(() => {
    try {
      const next = drawBalancingPractice(newSeed(), type === 'all' ? undefined : type);
      setPractice(next);
      setTyped(next.answer.map(() => ''));
      setVerdict(null);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [type]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (error) return <Banner kind="error">{error}</Banner>;
  if (!practice) return <p className="muted">Drawing a reaction…</p>;

  const species = [...practice.reactants, ...practice.products];

  return (
    <div className="stack">
      <select
        className="select"
        value={type}
        onChange={(e) => setType(e.target.value as ReactionType | 'all')}
        aria-label="Reaction type"
      >
        <option value="all">Every type</option>
        {(Object.keys(REACTION_TYPE_LABELS) as ReactionType[]).map((key) => (
          <option key={key} value={key}>
            {REACTION_TYPE_LABELS[key]}
          </option>
        ))}
      </select>

      <div className="card stack">
        <span className="chip chip--count">{REACTION_TYPE_LABELS[practice.type]}</span>

        <div className="row" style={{ gap: 4, alignItems: 'center' }}>
          {species.map((formula, i) => (
            <span key={`${formula}-${i}`} className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
              {i === practice.reactants.length ? (
                <span className="mono" style={{ margin: '0 6px' }}>
                  →
                </span>
              ) : i > 0 ? (
                <span className="mono">+</span>
              ) : null}
              <input
                className="input mono"
                style={{ width: 52, minHeight: 40, textAlign: 'center', padding: 4 }}
                inputMode="numeric"
                aria-label={`Coefficient for ${formula}`}
                value={typed[i] ?? ''}
                disabled={verdict?.correct}
                onChange={(e) => {
                  const next = [...typed];
                  next[i] = e.target.value.replace(/\D/g, '');
                  setTyped(next);
                }}
              />
              <span className="mono" style={{ fontSize: '1.05rem' }}>
                {formula}
              </span>
            </span>
          ))}
        </div>

        {verdict ? (
          <Banner kind={verdict.correct ? 'ok' : 'warn'}>{verdict.message}</Banner>
        ) : null}

        <div className="row">
          {!verdict?.correct ? (
            <button
              className="btn btn--primary"
              onClick={() =>
                setVerdict(
                  checkCoefficients(
                    practice,
                    typed.map((t) => (t === '' ? null : Number(t))),
                  ),
                )
              }
            >
              Check
            </button>
          ) : null}
          <button className="btn" onClick={draw}>
            {verdict?.correct ? 'Next reaction' : 'Skip'}
          </button>
          {!verdict?.correct ? (
            <button
              className="btn btn--ghost"
              onClick={() => {
                setTyped(practice.answer.map(String));
                setVerdict({ correct: false, message: 'Filled in for you — work out why each one is what it is.' });
              }}
            >
              Show me
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RedoxTab() {
  const [input, setInput] = useState('MnO4- + Fe2+ -> Mn2+ + Fe3+');
  const [medium, setMedium] = useState<Medium>('acid');
  const [result, setResult] = useState<RedoxOutcome | null>(null);

  return (
    <div className="stack">
      <label className="label" htmlFor="redox">
        A redox skeleton
      </label>
      <input
        id="redox"
        className="input mono"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setResult(balanceRedox(input, medium));
        }}
        placeholder="Cr2O7 2- + Fe2+ -> Cr3+ + Fe3+"
      />
      <span className="faint">
        Leave out the water, H+ and OH- — the half-reaction method puts them in.
      </span>

      <div className="switch-row">
        <span>Solution</span>
        <div className="seg">
          {(['acid', 'base'] as const).map((m) => (
            <button key={m} className="seg__btn" aria-pressed={medium === m} onClick={() => setMedium(m)}>
              {m === 'acid' ? 'Acidic' : 'Basic'}
            </button>
          ))}
        </div>
      </div>

      <button className="btn btn--primary" onClick={() => setResult(balanceRedox(input, medium))}>
        Work it through
      </button>

      {result && !result.ok ? <Banner kind="warn">{result.message}</Banner> : null}

      {result?.ok ? (
        <>
          <div className="card stack">
            <span className="flashcard__label">Balanced in {result.medium === 'acid' ? 'acid' : 'base'}</span>
            <div className="mono" style={{ fontSize: '1.1rem', overflowWrap: 'anywhere' }}>
              {result.equation}
            </div>
          </div>

          <div className="card stack">
            <span className="flashcard__label">The two halves</span>
            <div>
              <div className="faint">Oxidation — {result.oxidation.element} loses electrons</div>
              <div className="mono" style={{ overflowWrap: 'anywhere' }}>
                {halfText(result.oxidation)}
              </div>
            </div>
            <div>
              <div className="faint">Reduction — {result.reduction.element} gains electrons</div>
              <div className="mono" style={{ overflowWrap: 'anywhere' }}>
                {halfText(result.reduction)}
              </div>
            </div>
          </div>

          <h2 className="section-title">Every step</h2>
          <ol className="stack" style={{ paddingLeft: '1.2em', margin: 0 }}>
            {result.steps.map((step, i) => (
              <li key={i} style={{ overflowWrap: 'anywhere' }}>
                {step}
              </li>
            ))}
          </ol>
        </>
      ) : null}

      {!result ? (
        <EmptyState icon="⇌" title="Try one">
          Permanganate with iron(II) in acid, dichromate with ethanol in acid, or permanganate with
          iodide in base.
        </EmptyState>
      ) : null}
    </div>
  );
}

function halfText(half: { left: Array<{ formula: string; count: number }>; right: Array<{ formula: string; count: number }>; electrons: number; electronSide: 'left' | 'right' }): string {
  const side = (terms: Array<{ formula: string; count: number }>) =>
    terms.map((t) => (t.count === 1 ? t.formula : `${t.count} ${t.formula}`)).join(' + ');
  const left = side(half.left) + (half.electronSide === 'left' ? ` + ${half.electrons} e-` : '');
  const right = side(half.right) + (half.electronSide === 'right' ? ` + ${half.electrons} e-` : '');
  return `${left} → ${right}`;
}
